//! Import the necessary dependencies
import { useState, useCallback } from "react";

//* Import the necessary dependencies from the react-query library
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

//! Import the necessary dependencies
import { questionTagAPI } from "../questionsTags/questionsTagsAPI";

//? Import the necessary types from the types file
import {
  type GetQuestionTagsResponse,
} from "../questionsTags/types/types";

//* Type for pagination data
import { type Pagination } from "../../services/stores/types";

export const useQuestionTag = (token: string) => {
  const queryClient = useQueryClient();

  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 10,
    totalItems: 0,
  });
  const [questionIdFilter, setQuestionIdFilter] = useState<string | undefined>(
    undefined
  );

  const queryKey = [
    "questionTags",
    {
      search,
      currentPage: pagination.currentPage,
      perPage: pagination.itemsPerPage,
      questionId: questionIdFilter,
    },
  ] as const;

  const { data, isLoading, isError, error, refetch, isFetching } =
    useQuery<GetQuestionTagsResponse>({
      queryKey,
      queryFn: () =>
        questionTagAPI.getAll(
          search,
          pagination.currentPage,
          token,
          pagination.itemsPerPage,
          questionIdFilter
        ),
      staleTime: 5 * 60 * 1000,
    });

  const questionTags = data?.questionTags || [];
  const fetchedPagination = data?.pagination || pagination;

  const handleSearch = useCallback((searchTerm: string) => {
    setSearch(searchTerm);
    setPagination((prev:any) => ({ ...prev, currentPage: 1 }));
  }, []);

  const changePage = useCallback((newPage: number) => {
    setPagination((prev:any) => ({ ...prev, currentPage: newPage }));
  }, []);

  const setQuestionId = useCallback((questionId: string | undefined) => {
    setQuestionIdFilter(questionId);
    setPagination((prev:any) => ({ ...prev, currentPage: 1 }));
  }, []);

  const createMutation = useMutation({
    mutationFn: (input: {
      associations: { question_id: string; tag_id: string }[];
    }) => questionTagAPI.create(input, token),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionTags"] });
    },
  });


  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await questionTagAPI.delete([id], token);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questionTags"] });
    },
  });

  return {
    questionTags,
    loading: isLoading || isFetching,
    error: isError ? (error as Error)?.message : null,
    search,
    setSearch: handleSearch,
    pagination: fetchedPagination,
    setPagination,
    refetch,
    createRelations: createMutation.mutate,
    deleteRelations: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isDeleting: deleteMutation.isPending,
    changePage,
    setQuestionId,
  };
};
